import type { ReactNode, SyntheticEvent } from "react";
import { isValidElement, useEffect, useState } from "react";
import {
  MRT_EditActionButtons as MRTEditActionButtons,
  MRT_Row,
  MRT_TableInstance
} from "material-react-table";
import { Datepicker, locale, MbscDatepickerChangeEvent } from "@mobiscroll/react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  useMediaQuery,
  FormHelperText,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { Close, OpenInNew } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  FleetEventLocation,
  FleetEventRow,
  FleetEventType
} from "../FleetManagementEventDetailTable.component";
import { Colleague } from "../../../../features/Connections/types/Colleague.type";
import { getBookingAction, isCancelAllowed } from "../functions/getBookingAction.function";
import { BookingProperty } from "../../../FacilityManager/Domain/Types/FloorPlan/BookingProperty.type";
import { FleetManagementRow } from "../../types/FleetManagement-row.types";
import { FleetAttachment } from "../../types/FleetManagement.types";
import useTimeFormat from "../../../../hooks/useTimeFormat/useTimeFormat";
import { isEventEditable, isStartTimeEditable } from "../functions/isEventEditable.function";
import { getUserTitle } from "../functions/getUserTitle.function";
import { toIsoString } from "../functions/fleetEventSort.function";
import { calMinuteInterval } from "../../../BookingForm/functions/date-time.functions";
import { ButtonStyledText } from "../../../Common/ButtonStyledText.partial";
import { TransitionForSlideoutPanel } from "../../../SlideoutPanel/SlideoutPanelTransition.component";

export type RenderFleetEventUserCellOptions = {
  /** When true, append " (admin)" so it's clear this is the block creator, not the booking user. */
  isBlockingEvent?: boolean;
  /** Translated label for admin, e.g. t("Admin"). Shown as "FirstName Surname (Admin)". */
  adminLabel?: string;
  /** When set and different from user, show "Booked by: Name" in smaller font below (book-on-behalf). */
  userBookedBy?: Colleague | null;
  /** Translated label for "Booked by", e.g. t("_fleetEventDetail_bookedBy"). */
  bookedByLabel?: string;
};

export const renderFleetEventUserCell = (
  user: Colleague | null | undefined,
  _loggedInUserId?: string,
  _isEditable?: boolean,
  options?: RenderFleetEventUserCellOptions
) => {
  if (!user) return null;

  const { isBlockingEvent, adminLabel, userBookedBy, bookedByLabel } = options ?? {};
  const suffix = isBlockingEvent && adminLabel ? ` (${adminLabel})` : "";
  const fullName = getUserTitle(user);
  const showBookedBy =
    userBookedBy != null &&
    userBookedBy.userId !== user.userId &&
    bookedByLabel != null &&
    bookedByLabel !== "";

  return (
    <Stack component="span" direction="column" spacing={0} sx={{ py: 0 }}>
      <span>
        {fullName}
        {suffix}
      </span>
      {showBookedBy && (
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.75rem" }}
        >
          {bookedByLabel}: {getUserTitle(userBookedBy)}
        </Typography>
      )}
    </Stack>
  );
};

export const renderFleetEventLocationCell = (location?: FleetEventLocation | null | undefined) => {
  if (!location) return null;
  return <>{location.name}</>;
};

const getDraftValue = <T,>(row: MRT_Row<FleetEventRow>, key: string): T | undefined => {
  // MRT keeps in-progress edit values here (not part of public types)
  const r = row as MRT_Row<any>;
  const cache = r?._valuesCache as Record<string, unknown> | undefined;
  return (cache?.[key] as T | undefined) ?? (r.original?.[key] as T | undefined);
};

export const renderBookingUserEdit = ({
  row,
  users,
  draft,
  validationError,
  onClearValidation,
  onDraftChange
}: {
  row: MRT_Row<FleetEventRow>;
  users: Colleague[] | undefined;
  draft?: Partial<FleetEventRow>;
  validationError?: string;
  /** Called when user focuses the control to clear this field's validation (equipment-list pattern). */
  onClearValidation?: () => void;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
}) => {
  return (
    <EditBookingUserForFleetEvent
      row={row}
      users={users}
      draft={draft}
      validationError={validationError}
      onClearValidation={onClearValidation}
      onDraftChange={onDraftChange}
    />
  );
};

export function EditBookingUserForFleetEvent({
  row,
  users,
  draft,
  validationError,
  onClearValidation,
  onDraftChange
}: Readonly<{
  row: MRT_Row<FleetEventRow>;
  users: Colleague[] | undefined;
  draft?: Partial<FleetEventRow>;
  validationError?: string;
  onClearValidation?: () => void;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
}>) {
  const { t } = useTranslation();

  const effectiveEventType =
    draft?.eventType ??
    getDraftValue<FleetEventRow["eventType"]>(row, "eventType") ??
    row.original.eventType;
  const isBooking = effectiveEventType === "booking";

  const currentUserId = draft?.user?.userId ?? row.original.user?.userId;
  const value = currentUserId && users ? users.find(u => u.userId === currentUserId) ?? null : null;

  const handleUserChange = (_: SyntheticEvent, newValue: Colleague | null) => {
    if (!users) return;
    if (newValue) {
      onDraftChange({ user: newValue });
      row.original.user = newValue;
    } else {
      onDraftChange({ user: undefined });
      row.original.user = undefined;
    }
  };

  return (
    <Autocomplete<Colleague>
      data-testid="fleet-detail-event-bookingUser-select"
      fullWidth
      options={users ?? []}
      value={value}
      onChange={handleUserChange}
      disabled={!isBooking}
      getOptionLabel={option => `${option.firstName} ${option.surname}`}
      isOptionEqualToValue={(a, b) => a.userId === b.userId}
      renderInput={params => (
        <TextField
          {...params}
          variant="standard"
          required={true}
          error={!!validationError}
          helperText={validationError}
          label={t("Booking User")}
          placeholder={value == null ? t("Select user") : undefined}
          onFocus={onClearValidation}
        />
      )}
    />
  );
}

export const renderBookingLocationEdit = ({
  row,
  locationOptions,
  draft,
  onDraftChange
}: {
  row: MRT_Row<FleetEventRow>;
  locationOptions: Array<{ id: number; name: string }> | undefined;
  draft?: Partial<FleetEventRow>;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
}) => {
  return (
    <EditBookingLocationForFleetEvent
      row={row}
      locationOptions={locationOptions}
      draft={draft}
      onDraftChange={onDraftChange}
    />
  );
};

export function EditBookingLocationForFleetEvent({
  row,
  locationOptions,
  draft,
  onDraftChange
}: Readonly<{
  row: MRT_Row<FleetEventRow>;
  locationOptions: Array<{ id: number; name: string }> | undefined;
  draft?: Partial<FleetEventRow>;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
}>) {
  const { t } = useTranslation();

  const effectiveEventType =
    draft?.eventType ??
    getDraftValue<FleetEventRow["eventType"]>(row, "eventType") ??
    row.original.eventType;
  const isBooking = effectiveEventType === "booking";

  type LocationOption = { id: number; name: string };
  const currentId = draft?.location?.id ?? row.original.location?.id;
  const value =
    currentId != null && locationOptions
      ? locationOptions.find(l => l.id === currentId) ?? null
      : null;

  const handleLocationChange = (_: SyntheticEvent, newValue: LocationOption | null) => {
    if (newValue) {
      onDraftChange({ location: { id: newValue.id, name: newValue.name } });
      row.original.location = { id: newValue.id, name: newValue.name };
    } else {
      onDraftChange({ location: undefined });
      row.original.location = undefined;
    }
  };

  return (
    <Autocomplete<LocationOption>
      data-testid="fleet-detail-event-location-select"
      fullWidth
      options={locationOptions ?? []}
      value={value}
      onChange={handleLocationChange}
      disabled={!isBooking}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={params => (
        <TextField
          {...params}
          variant="standard"
          label={t("Location")}
          required={true}
          placeholder={value == null ? t("Select location") : undefined}
        />
      )}
    />
  );
}

export const renderFleetEventEdit = ({
  row,
  title,
  draft,
  timezone,
  maxDate,
  validationError,
  onClearValidation,
  onDraftChange
}: {
  row: MRT_Row<FleetEventRow>;
  title: string;
  draft?: Partial<FleetEventRow>;
  timezone: string;
  /** Max date for start/end (from max advanced booking settings for vehicle). */
  maxDate?: Date;
  validationError?: string;
  /** Called when user focuses the control to clear this field's validation (equipment-list pattern). */
  onClearValidation?: () => void;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
}) => {
  return (
    <EditFleetEventDateTime
      row={row}
      title={title}
      draft={draft}
      timezone={timezone}
      maxDate={maxDate}
      validationError={validationError}
      onClearValidation={onClearValidation}
      onDraftChange={onDraftChange}
    />
  );
};

export const EditFleetEventDateTime = ({
  row,
  title,
  draft,
  timezone,
  maxDate,
  validationError,
  onClearValidation,
  onDraftChange
}: {
  row: MRT_Row<FleetEventRow>;
  title: string;
  draft?: Partial<FleetEventRow>;
  timezone: string;
  /** Max date for start/end (from max advanced booking settings for vehicle). */
  maxDate?: Date;
  validationError?: string;
  onClearValidation?: () => void;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
}) => {
  const isStart = title === "start";
  const fieldKey = isStart ? "start" : "end";
  const rawValue =
    draft?.[fieldKey] ?? getDraftValue<string | Date>(row, fieldKey) ?? row.original[fieldKey];
  const currentValueIso = toIsoString(rawValue);
  const isEditable =
    row.id === "mrt-row-create" ||
    (isStart ? isStartTimeEditable(row, timezone) : isEventEditable(row, timezone));

  const { t, i18n } = useTranslation();
  const { dateFormat, timeFormat } = useTimeFormat();
  const dateFormatFull = dateFormat + " " + timeFormat;
  const pickerLabel = isStart ? t("Start Date & Time") : t("End Date & Time");
  const [newValue, setNewValue] = useState<string | null>(currentValueIso ?? null);

  // When editing "start", min is now rounded up to next quarter hour; when editing "end", min is the (possibly edited) "start".
  // When start is not editable, use the event's start as min so the datepicker does not coerce the value to "latest quarter".
  const draftStartRaw =
    draft?.start ?? getDraftValue<string | Date>(row, "start") ?? row.original.start;
  const draftStartIso = toIsoString(draftStartRaw);
  const draftStartDate = draftStartIso ? DateTime.fromISO(draftStartIso).toJSDate() : undefined;

  let minValue: Date | undefined;
  if (isStart && isEditable) {
    const now = DateTime.now();
    minValue = now
      .set({
        minute: calMinuteInterval(now),
        second: 0,
        millisecond: 0
      })
      .toJSDate();
  } else {
    minValue = draftStartDate;
  }

  const handleEventTimeChange = (picker: MbscDatepickerChangeEvent) => {
    if (!isEditable) return;
    if (picker.value) {
      const newVal = DateTime.fromJSDate(picker.value as Date);
      const isoValue = newVal.toISO();

      if (!isoValue) return;

      // Material React Table tracks changes through row.original
      if (isStart) {
        row.original.start = isoValue;
        onDraftChange({ start: isoValue });
      } else {
        row.original.end = isoValue;
        onDraftChange({ end: isoValue });
      }
      setNewValue(isoValue);
    }
  };

  const pickerValue = newValue
    ? (() => {
        const d = new Date(newValue);
        return Number.isFinite(d.getTime()) ? d : null;
      })()
    : null;

  return (
    <Stack
      spacing={0.5}
      onFocusCapture={onClearValidation}
      sx={{
        width: "100%",
        "& .mbsc-material.mbsc-textfield-wrapper": { m: 0, width: "100%" },
        "& .mbsc-material.mbsc-textfield": { width: "100%" }
      }}
    >
      <Datepicker
        controls={["calendar", "time"]}
        stepMinute={15}
        disabled={!isEditable}
        value={pickerValue}
        dateFormatFull={dateFormatFull}
        onChange={picker => handleEventTimeChange(picker)}
        locale={locale[i18n.language]}
        min={minValue}
        max={maxDate}
        inputProps={{ label: pickerLabel, inputClass: "mbsc-material-datepicker-squeezed" }}
      />
      {validationError ? <FormHelperText error>{validationError}</FormHelperText> : null}
    </Stack>
  );
};

export const renderFleetEventActions = ({
  row,
  timezone,
  checkinButtonLabel,
  checkoutButtonLabel,
  cancelButtonLabel,
  handleCheckIn,
  handleCheckOut,
  handleCancel
}: {
  row: MRT_Row<FleetEventRow>;
  timezone: string;
  checkinButtonLabel: string;
  checkoutButtonLabel: string;
  cancelButtonLabel: string;
  handleCheckIn: (bookingId: number, initialOdometer?: number | null) => void;
  handleCheckOut: (bookingId: number, initialOdometer?: number | null) => void;
  handleCancel: (bookingId: number) => void;
}) => {
  // Don't show for blocked events
  if (row.original.eventType === FleetEventType.BLOCKING) return null;

  const bookingId = row.original.bookingId;
  const hasBookingId = bookingId != null;

  if (!hasBookingId) return null;

  const initialOdometer = row.original.odometer ?? null;

  // Get the primary action from getBookingAction (check-in / check-out / null)
  const action = getBookingAction(
    row.original.start,
    row.original.end,
    timezone,
    row.original.isCheckedIn,
    row.original.isCheckedOut
  );
  // Admin can cancel anytime until the event is in the past or the trip is checked out
  const canCancel =
    row.original.eventType === FleetEventType.BOOKING &&
    isCancelAllowed(row.original.end, timezone);

  if (action === "check-in") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 130 }}>
        <Button
          data-testid={`${checkinButtonLabel}-btn`}
          onClick={() => handleCheckIn(bookingId, initialOdometer)}
        >
          {checkinButtonLabel}
        </Button>

        {canCancel && (
          <Button
            data-testid={`${cancelButtonLabel}-btn`}
            onClick={() => handleCancel(bookingId)}
            color="secondary"
          >
            {cancelButtonLabel}
          </Button>
        )}
      </Box>
    );
  }

  if (action === "check-out") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 130 }}>
        <Button
          data-testid={`${checkoutButtonLabel}-btn`}
          onClick={() => handleCheckOut(bookingId, initialOdometer)}
        >
          {checkoutButtonLabel}
        </Button>
        {canCancel && (
          <Button
            data-testid={`${cancelButtonLabel}-btn`}
            onClick={() => handleCancel(bookingId)}
            color="secondary"
          >
            {cancelButtonLabel}
          </Button>
        )}
      </Box>
    );
  }

  // No primary action (check-in/check-out) but cancel still allowed (event not in past), show cancel.
  if (canCancel) {
    return (
      <Button
        data-testid={`${cancelButtonLabel}-btn`}
        onClick={() => handleCancel(bookingId)}
        color="secondary"
      >
        {cancelButtonLabel}
      </Button>
    );
  }

  return null;
};

export type RenderReasonEditProps = {
  row: MRT_Row<FleetEventRow>;
  draft?: Partial<FleetEventRow>;
  validationError?: string;
  /** Called when user focuses the control to clear this field's validation (equipment-list pattern). */
  onClearValidation?: () => void;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
};

export const renderReasonEdit = ({
  row,
  draft,
  validationError,
  onClearValidation,
  onDraftChange
}: RenderReasonEditProps) => {
  return (
    <EditBlockReason
      row={row}
      draft={draft}
      validationError={validationError}
      onClearValidation={onClearValidation}
      onDraftChange={onDraftChange}
    />
  );
};

export const EditBlockReason = ({
  row,
  draft,
  validationError,
  onClearValidation,
  onDraftChange
}: {
  row: MRT_Row<FleetEventRow>;
  draft?: Partial<FleetEventRow>;
  validationError?: string;
  onClearValidation?: () => void;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
}) => {
  const { t } = useTranslation();

  const effectiveEventType =
    draft?.eventType ??
    getDraftValue<FleetEventRow["eventType"]>(row, "eventType") ??
    row.original.eventType;
  const isBlocking = effectiveEventType === FleetEventType.BLOCKING;

  const currentVal =
    draft?.reason ?? getDraftValue<string>(row, "reason") ?? row.original.reason ?? "";

  return (
    <TextField
      label={isBlocking ? t("Reason for Blocking") : t("Reason for Booking")}
      variant="standard"
      fullWidth
      value={String(currentVal)}
      error={!!validationError}
      helperText={validationError}
      onFocus={onClearValidation}
      onChange={e => {
        const value = e.target.value;
        onDraftChange({ reason: value });
        row.original.reason = value;
      }}
    />
  );
};

export const renderFleetEventTypeCell = (
  eventType: FleetEventRow["eventType"],
  t: (key: string) => string
) => {
  return <>{eventType === FleetEventType.BOOKING ? t("Booking") : t("Blocking")}</>;
};

/** Renders vehicle name for event row when in all-events mode. */
export const renderFleetEventVehicleCell = (
  vehicleId: number | undefined,
  allFleetData: FleetManagementRow[] | undefined
) => {
  if (vehicleId == null || !allFleetData?.length) {
    return null;
  }
  const vehicle = allFleetData.find(f => f.id === vehicleId);

  return <>{vehicle?.name ?? `#${vehicleId}`}</>;
};

/** Vehicle select for create-row when in all-events mode. */
export function EditFleetEventVehicle({
  row,
  allFleetData,
  draft,
  onDraftChange
}: Readonly<{
  row: MRT_Row<FleetEventRow>;
  allFleetData: FleetManagementRow[];
  draft?: Partial<FleetEventRow>;
  onDraftChange: (patch: Partial<FleetEventRow>) => void;
}>) {
  const { t } = useTranslation();
  const currentVehicleId = draft?.vehicleId ?? row.original.vehicleId;
  const value =
    currentVehicleId == null ? null : allFleetData.find(f => f.id === currentVehicleId) ?? null;

  const handleVehicleChange = (_: SyntheticEvent, newValue: FleetManagementRow | null) => {
    if (newValue) {
      onDraftChange({ vehicleId: newValue.id });
      (row.original as FleetEventRow).vehicleId = newValue.id;
    } else {
      onDraftChange({ vehicleId: undefined });
      (row.original as FleetEventRow).vehicleId = undefined;
    }
  };

  return (
    <Autocomplete<FleetManagementRow>
      data-testid="fleet-detail-event-vehicle-select"
      fullWidth
      options={allFleetData}
      value={value}
      onChange={handleVehicleChange}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={params => (
        <TextField
          {...params}
          variant="standard"
          label={t("Vehicle")}
          placeholder={t("Select vehicle")}
        />
      )}
    />
  );
}

export const renderFleetEventVehicleEdit = (props: Parameters<typeof EditFleetEventVehicle>[0]) => (
  <EditFleetEventVehicle {...props} />
);

export const renderFleetPropertiesCell = (properties: BookingProperty[]) => {
  return (
    <> {properties && properties.length > 0 ? properties.map(prop => prop.name).join(", ") : "-"}</>
  );
};

export const renderFleetContractDetailsCell = (
  original: FleetManagementRow,
  t: (key: string) => string,
  dateFormat: string
) => {
  const contract = original.contractDetails;

  if (!contract || contract.vendor === "") return <>{t("No contract details")}</>;

  const dateRangeLabel = (() => {
    if (!contract.start || !contract.end) return null;

    const startDt = DateTime.fromISO(contract.start);
    const endDt = DateTime.fromISO(contract.end);

    const startLabel = startDt.isValid ? startDt.toFormat(dateFormat) : contract.start;
    const endLabel = endDt.isValid ? endDt.toFormat(dateFormat) : contract.end;

    return `${startLabel} ~ ${endLabel}`;
  })();

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {contract.vendor && <Chip label={contract.vendor} size="small" variant="outlined" />}
      {contract.contractId && (
        <Chip label={`ID: ${contract.contractId}`} size="small" variant="outlined" />
      )}
      {dateRangeLabel && <Chip label={dateRangeLabel} size="small" variant="outlined" />}
      {contract.leaseRate && contract.leaseRate > 0 && (
        <Chip label={`Rate: ${contract.leaseRate}`} size="small" variant="outlined" />
      )}
    </Stack>
  );
};

export function FleetAttachmentsDialog({
  attachments,
  open,
  onClose,
  t
}: Readonly<{
  attachments: FleetAttachment[];
  open: boolean;
  onClose: () => void;
  t: (key: string) => string;
}>) {
  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {t("Attachments")}
        <IconButton aria-label={t("Close")} onClick={onClose} size="small" sx={{ ml: 1 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{t("Name")}</TableCell>
                <TableCell align="left">{t("Link")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attachments.map((att, index) => (
                <TableRow key={att.id ?? att.path ?? att.filename ?? index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{att.filename ?? att.file?.name ?? "—"}</TableCell>
                  <TableCell align="left">
                    {att.downloadUrl ? (
                      <Link
                        href={att.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        component="a"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                      >
                        {t("Open")}
                        <OpenInNew fontSize="small" sx={{ verticalAlign: "middle" }} />
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}

export function FleetAttachmentsCellContent({
  original,
  t,
  isEditable
}: Readonly<{
  original: FleetManagementRow;
  t: (key: string) => string;
  isEditable: boolean;
}>) {
  const theme = useTheme();

  const [dialogOpen, setDialogOpen] = useState(false);
  const all = original.attachments ?? [];
  const visible = isEditable ? all : all.filter(att => !att.isAdminOnly);
  const attachmentsCount = visible.length;

  if (!attachmentsCount) return <>{t("No attachments")}</>;

  const adminOnlyCount = isEditable ? all.filter(att => att.isAdminOnly).length : 0;
  const attachmentsLabel = attachmentsCount === 1 ? t("Attachment") : t("Attachments");
  const buttonColor =
    theme.palette.mode === "dark" ? theme.palette.text.primary : theme.palette.text.secondary;

  return (
    <>
      <ButtonStyledText
        theme={theme}
        sx={{ color: buttonColor }}
        onClick={() => setDialogOpen(true)}
      >
        {attachmentsCount} {attachmentsLabel}
        {isEditable && adminOnlyCount ? ` • ${adminOnlyCount} ${t("admin only")}` : ""}
      </ButtonStyledText>

      <FleetAttachmentsDialog
        attachments={visible}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        t={t}
      />
    </>
  );
}

export const renderFleetAttachmentsCell = (
  original: FleetManagementRow,
  t: (key: string) => string,
  isEditable: boolean
) => <FleetAttachmentsCellContent original={original} t={t} isEditable={isEditable} />;

export const editFleetProperties = (
  original: FleetManagementRow,
  remoteBookingProperty: BookingProperty[] | undefined,
  propertiesDraft: BookingProperty[],
  setPropertiesDraft: (
    value: BookingProperty[] | ((prev: BookingProperty[]) => BookingProperty[])
  ) => void
) => {
  return (
    <EditFleetProperties
      remoteBookingProperty={remoteBookingProperty}
      propertiesDraft={propertiesDraft}
      setPropertiesDraft={setPropertiesDraft}
      original={original}
    />
  );
};

export const EditFleetProperties = ({
  remoteBookingProperty = [],
  propertiesDraft,
  setPropertiesDraft,
  original
}: Readonly<{
  remoteBookingProperty: BookingProperty[] | undefined;
  propertiesDraft: BookingProperty[];
  setPropertiesDraft: (
    value: BookingProperty[] | ((prev: BookingProperty[]) => BookingProperty[])
  ) => void;
  original: FleetManagementRow;
}>) => {
  const { t } = useTranslation();
  const currentValue = Array.isArray(propertiesDraft) ? propertiesDraft : [];
  const options = Array.isArray(remoteBookingProperty) ? remoteBookingProperty : [];
  const [local, setLocal] = useState<BookingProperty[]>([]);

  useEffect(() => {
    setLocal(currentValue);
  }, []);

  return (
    <Autocomplete
      multiple
      fullWidth
      disableCloseOnSelect
      options={options}
      getOptionLabel={option => option.name}
      getOptionKey={option => option.id.toString()}
      renderInput={params => <TextField {...params} label={t("Properties")} />}
      onChange={(_, value) => {
        setLocal(value);
        setPropertiesDraft(value);
        original.properties = value;
      }}
      value={local}
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          return <Chip key={key} {...rest} label={option.name} />;
        });
      }}
    />
  );
};

export function FleetEventSlidePanelEditRow({
  table,
  row,
  internalEditComponents,
  headline
}: Readonly<{
  table: MRT_TableInstance<FleetEventRow>;
  row: MRT_Row<FleetEventRow>;
  internalEditComponents: ReactNode[];
  headline: string;
}>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const editDialogStyle = {
    height: "calc(-64px + 100vh)",
    maxHeight: "calc(-64px + 100vh)",
    maxWidth: isMobile ? "inherit" : "50vw",
    position: "absolute",
    right: 0,
    margin: 0,
    width: isMobile ? "100%" : "40vw",
    padding: 3,
    top: "64px"
  };

  return (
    <Dialog
      open
      data-testid="fleet-event-edit-row-dialog"
      slotProps={{ paper: { sx: { ...editDialogStyle } } }}
      transitionDuration={400}
      slots={{ transition: TransitionForSlideoutPanel }}
    >
      <DialogTitle variant="h4">{headline}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", mt: 2 }}>
        <Grid2 container data-testid="fleet-event-edit-dialog-contents" columnGap={4}>
          <Grid2 container size={{ xs: 12 }} direction="column" rowGap={3.5}>
            {internalEditComponents.map((component, index) => {
              if (index === 0) return null;
              const fieldKey =
                isValidElement(component) && component.key != null
                  ? String(component.key)
                  : `fleet-event-edit-field-${index}`;
              return (
                <Grid2 key={fieldKey} size={{ xs: 12 }} sx={{ width: "100%" }}>
                  {component}
                </Grid2>
              );
            })}
          </Grid2>
        </Grid2>
      </DialogContent>
      <Divider sx={{ mt: 3, mb: 3, opacity: "0.6" }} />
      <DialogActions>
        <MRTEditActionButtons variant="text" table={table} row={row} />
      </DialogActions>
    </Dialog>
  );
}
